import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;
const PAN_STEP = 50;
const ROTATE_STEP = 90;
const ORIGIN = { x: 0, y: 0 };

const useStyles = makeStyles((theme) => ({
	content: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		height: '85vh',
		padding: 0,
		overflow: 'hidden',
		'&:first-child': {
			paddingTop: 0
		}
	},
	stage: {
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		minHeight: 0,
		overflow: 'hidden',
		backgroundColor: '#000'
	},
	image: {
		width: 'auto',
		height: 'auto',
		maxWidth: 'min(800px, 100%)',
		maxHeight: '100%',
		objectFit: 'contain',
		transformOrigin: 'center center',
		userSelect: 'none',
		transform: ({ x, y, rotation, zoom }) => `translate(${x}px, ${y}px) rotate(${rotation}deg) scale(${zoom})`,
		transition: ({ dragging, instant }) => (dragging || instant ? 'none' : 'transform 0.2s ease-out'),
		cursor: ({ zoom, dragging }) => (zoom > MIN_ZOOM ? (dragging ? 'grabbing' : 'grab') : 'default')
	},
	hint: {
		position: 'absolute',
		top: 20,
		left: 20,
		zIndex: 10,
		padding: theme.spacing(1, 1.5),
		borderRadius: 6,
		color: '#fff',
		backgroundColor: 'rgba(0,0,0,0.7)',
		fontSize: 14,
		fontWeight: 500,
		opacity: 0.8,
		pointerEvents: 'none'
	},
	navBtn: {
		position: 'absolute',
		top: '50%',
		zIndex: 10,
		width: 44,
		height: 44,
		color: '#fff',
		backgroundColor: 'rgba(0,0,0,0.5)',
		transform: 'translateY(-50%)',
		'&:hover': {
			backgroundColor: 'rgba(0,0,0,0.7)'
		},
		'&.Mui-disabled': {
			color: 'rgba(255,255,255,0.3)',
			backgroundColor: 'rgba(0,0,0,0.2)'
		}
	},
	navPrev: {
		left: 16
	},
	navNext: {
		right: 16
	},
	counter: {
		position: 'absolute',
		top: 16,
		right: 16,
		zIndex: 10,
		padding: theme.spacing(0.5, 1.5),
		borderRadius: 20,
		color: '#fff',
		backgroundColor: 'rgba(0,0,0,0.6)',
		fontSize: 13,
		fontWeight: 600,
		letterSpacing: '0.5px'
	},
	controlBar: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 12,
		padding: '14px 24px',
		backgroundColor: theme.palette.primary.main,
		boxShadow: '0 -4px 20px rgba(0,0,0,0.08)'
	},
	group: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		padding: '6px 8px',
		borderRadius: 12,
		backgroundColor: 'rgba(255,255,255,0.95)',
		boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
	},
	controlBtn: {
		width: 34,
		height: 34,
		padding: 0,
		borderRadius: 8,
		color: '#1a1a1a',
		backgroundColor: '#f0f0f0',
		transition: 'all 0.15s ease',
		'&:hover': {
			backgroundColor: '#e0e0e0'
		},
		'&.Mui-disabled': {
			color: '#aaa',
			backgroundColor: 'transparent'
		},
		'& svg': {
			display: 'block',
			fontSize: 18
		}
	},
	padGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 30px)',
		gap: 2
	},
	padBtn: {
		width: 30,
		height: 30,
		borderRadius: 6,
		'& svg': {
			fontSize: 16
		}
	},
	value: {
		minWidth: 64,
		padding: '4px 8px',
		color: '#1a1a1a',
		fontSize: 13,
		fontWeight: 700,
		textAlign: 'center',
		letterSpacing: '-0.3px'
	},
	valueNarrow: {
		minWidth: 48,
		padding: '4px 6px',
		fontSize: 12
	},
	resetBtn: {
		padding: '8px 18px',
		borderRadius: 10,
		color: '#fff',
		backgroundColor: 'rgba(26,26,26,0.9)',
		boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
		fontSize: 13,
		fontWeight: 600,
		letterSpacing: '0.2px',
		textTransform: 'none',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: '#000'
		},
		'&.Mui-disabled': {
			color: '#999',
			backgroundColor: 'rgba(255,255,255,0.6)',
			boxShadow: 'none'
		}
	},
	saveBtn: {
		backgroundColor: 'rgba(102,187,106,0.9)',
		boxShadow: '0 2px 8px rgba(102,187,106,0.25)',
		'&:hover': {
			backgroundColor: '#43a047'
		}
	},
	saveSpinner: {
		display: 'block',
		marginRight: theme.spacing(1)
	}
}));

const roundZoom = (value) => Math.round(value * 10) / 10;

// onSaveRotation(src, angle) is optional: when given, a Save button persists the rotation.
// It must return a promise resolving to true once the rotated image has replaced `src` in `images`.
const PhotoViewer = ({ open, images, index, onClose, onSaveRotation }) => {
	const [gState] = useContext(AppContext);
	const language = gState.translation || PersistantData.getInstance().getlang();

	const [current, setCurrent] = useState(0);
	const [zoom, setZoom] = useState(MIN_ZOOM);
	const [position, setPosition] = useState(ORIGIN);
	const [rotation, setRotation] = useState(0);
	const [dragging, setDragging] = useState(false);
	const [saving, setSaving] = useState(false);
	const [instant, setInstant] = useState(false);
	const dragRef = useRef(null);
	// src being replaced by a saved rotation: its rotation stays on screen until the new file has loaded
	const pendingSrcRef = useRef(null);
	const srcRef = useRef(null);

	const classes = useStyles({ x: position.x, y: position.y, rotation, zoom, dragging, instant });

	const list = Array.isArray(images) ? images : [];
	const total = list.length;
	const canNavigate = total > 1;
	const canGoPrev = current > 0;
	const canGoNext = current < total - 1;
	const isDefaultView = zoom === MIN_ZOOM && position.x === 0 && position.y === 0 && rotation === 0;

	const resetView = useCallback(() => {
		pendingSrcRef.current = null;
		setZoom(MIN_ZOOM);
		setPosition(ORIGIN);
		setRotation(0);
		setDragging(false);
	}, []);

	useEffect(() => {
		if (open) {
			setCurrent(index > 0 ? index : 0);
			resetView();
		}
	}, [open, index, resetView]);

	useEffect(() => {
		if (!instant) {
			return undefined;
		}
		const frame = requestAnimationFrame(() => setInstant(false));
		return () => cancelAnimationFrame(frame);
	}, [instant]);

	useEffect(() => {
		if (!dragging) {
			return undefined;
		}
		const handleMouseMove = (e) => {
			const start = dragRef.current;
			setPosition({ x: start.originX + e.clientX - start.startX, y: start.originY + e.clientY - start.startY });
		};
		const handleMouseUp = () => {
			setDragging(false);
		};
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [dragging]);

	const handleMouseDown = useCallback((e) => {
		if (zoom <= MIN_ZOOM) {
			return;
		}
		dragRef.current = { startX: e.clientX, startY: e.clientY, originX: position.x, originY: position.y };
		setDragging(true);
		e.preventDefault();
	}, [zoom, position]);

	const handleZoomOut = useCallback(() => {
		const next = Math.max(MIN_ZOOM, roundZoom(zoom - ZOOM_STEP));
		setZoom(next);
		if (next === MIN_ZOOM) {
			setPosition(ORIGIN);
		}
	}, [zoom]);

	const handleZoomIn = useCallback(() => {
		setZoom(Math.min(MAX_ZOOM, roundZoom(zoom + ZOOM_STEP)));
	}, [zoom]);

	const handlePanUp = useCallback(() => {
		setPosition(prev => ({ x: prev.x, y: prev.y + PAN_STEP }));
	}, []);

	const handlePanDown = useCallback(() => {
		setPosition(prev => ({ x: prev.x, y: prev.y - PAN_STEP }));
	}, []);

	const handlePanLeft = useCallback(() => {
		setPosition(prev => ({ x: prev.x + PAN_STEP, y: prev.y }));
	}, []);

	const handlePanRight = useCallback(() => {
		setPosition(prev => ({ x: prev.x - PAN_STEP, y: prev.y }));
	}, []);

	const handleRotateLeft = useCallback(() => {
		setRotation(prev => prev - ROTATE_STEP);
	}, []);

	const handleRotateRight = useCallback(() => {
		setRotation(prev => prev + ROTATE_STEP);
	}, []);

	const handlePrev = useCallback(() => {
		if (current > 0) {
			setCurrent(current - 1);
			resetView();
		}
	}, [current, resetView]);

	const handleNext = useCallback(() => {
		if (current < total - 1) {
			setCurrent(current + 1);
			resetView();
		}
	}, [current, total, resetView]);

	const handleClose = useCallback(() => {
		setDragging(false);
		onClose();
	}, [onClose]);

	const canPan = zoom > MIN_ZOOM;
	const src = list[current];
	srcRef.current = src;
	const angle = ((rotation % 360) + 360) % 360;

	// The saved file is already rotated, so swap it in with no animation: the old view never turns back first.
	const finishSwap = useCallback(() => {
		if (pendingSrcRef.current === null) {
			return;
		}
		pendingSrcRef.current = null;
		setInstant(true);
		setRotation(0);
		setSaving(false);
	}, []);

	const handleImageLoad = useCallback(() => {
		if (pendingSrcRef.current !== null && pendingSrcRef.current !== srcRef.current) {
			finishSwap();
		}
	}, [finishSwap]);

	const handleSave = useCallback(() => {
		if (!onSaveRotation || saving || angle === 0) {
			return;
		}
		setSaving(true);
		pendingSrcRef.current = src;
		Promise.resolve(onSaveRotation(src, angle)).then((saved) => {
			if (!saved) {
				pendingSrcRef.current = null;
				setSaving(false);
			} else if (srcRef.current === src) {
				// the url did not change, so there is no new load to wait for
				finishSwap();
			}
		}).catch(() => {
			pendingSrcRef.current = null;
			setSaving(false);
		});
	}, [onSaveRotation, saving, angle, src, finishSwap]);

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const handleKeyDown = (e) => {
			const tag = e.target && e.target.tagName;
			if (e.altKey || e.ctrlKey || e.metaKey || tag === 'INPUT' || tag === 'TEXTAREA') {
				return;
			}
			if (e.key === '+' || e.key === '=') {
				handleZoomIn();
			} else if (e.key === '-') {
				handleZoomOut();
			} else if (e.key.indexOf('Arrow') === 0) {
				// zoomed in the arrows pan the image, otherwise left/right switch image
				e.preventDefault();
				if (canPan) {
					({ ArrowUp: handlePanUp, ArrowDown: handlePanDown, ArrowLeft: handlePanLeft, ArrowRight: handlePanRight })[e.key]();
				} else if (!saving) {
					if (e.key === 'ArrowLeft') {
						handlePrev();
					} else if (e.key === 'ArrowRight') {
						handleNext();
					}
				}
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open, canPan, saving, handleZoomIn, handleZoomOut, handlePanUp, handlePanDown, handlePanLeft, handlePanRight, handlePrev, handleNext]);

	return (
		<Dialog fullWidth maxWidth="lg" open={open && total > 0} onClose={handleClose}>
			<DialogContent className={classes.content}>
				<div className={classes.stage}>
					{src && (
						<img
							src={src}
							alt={`${language.photo} ${current + 1}`}
							draggable={false}
							className={classes.image}
							onMouseDown={handleMouseDown}
							onLoad={handleImageLoad}
							onError={finishSwap}
						/>
					)}

					{canPan && !dragging && (
						<div className={classes.hint}>{language.photo_viewer_drag_hint}</div>
					)}

					{canNavigate && (
						<>
							<IconButton className={`${classes.navBtn} ${classes.navPrev}`} onClick={handlePrev} disabled={!canGoPrev || saving}>
								<ArrowBackIcon/>
							</IconButton>
							<IconButton className={`${classes.navBtn} ${classes.navNext}`} onClick={handleNext} disabled={!canGoNext || saving}>
								<ArrowForwardIcon/>
							</IconButton>
							<div className={classes.counter}>{current + 1} / {total}</div>
						</>
					)}
				</div>

				<div className={classes.controlBar}>
					<div className={classes.group}>
						<IconButton className={classes.controlBtn} onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM}>
							<RemoveIcon/>
						</IconButton>
						<Typography className={classes.value}>{Math.round(zoom * 100)}%</Typography>
						<IconButton className={classes.controlBtn} onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM}>
							<AddIcon/>
						</IconButton>
					</div>

					<div className={classes.group}>
						<div className={classes.padGrid}>
							<div/>
							<IconButton className={`${classes.controlBtn} ${classes.padBtn}`} onClick={handlePanUp} disabled={!canPan}>
								<ArrowUpwardIcon/>
							</IconButton>
							<div/>
							<IconButton className={`${classes.controlBtn} ${classes.padBtn}`} onClick={handlePanLeft} disabled={!canPan}>
								<ArrowBackIcon/>
							</IconButton>
							<IconButton className={`${classes.controlBtn} ${classes.padBtn}`} onClick={handlePanDown} disabled={!canPan}>
								<ArrowDownwardIcon/>
							</IconButton>
							<IconButton className={`${classes.controlBtn} ${classes.padBtn}`} onClick={handlePanRight} disabled={!canPan}>
								<ArrowForwardIcon/>
							</IconButton>
						</div>
					</div>

					<div className={classes.group}>
						<IconButton className={classes.controlBtn} onClick={handleRotateLeft} disabled={saving}>
							<RotateLeftIcon/>
						</IconButton>
						<Typography className={`${classes.value} ${classes.valueNarrow}`}>
							{angle}°
						</Typography>
						<IconButton className={classes.controlBtn} onClick={handleRotateRight} disabled={saving}>
							<RotateRightIcon/>
						</IconButton>
					</div>

					<Button className={classes.resetBtn} onClick={resetView} disabled={isDefaultView}>
						{language.reset}
					</Button>

					{onSaveRotation && (
						<Button
							className={`${classes.resetBtn} ${classes.saveBtn}`}
							onClick={handleSave}
							disabled={angle === 0 || saving}>
							{saving && <CircularProgress size={14} color="inherit" className={classes.saveSpinner}/>}
							{language.save}
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PhotoViewer;
